export const ENV = 'develop'; // local

export type DatadogConfig = {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  trackUserInteractions: boolean;
  trackResources: boolean;
  trackLongTasks: boolean;
  defaultPrivacyLevel: string;
  forwardErrorsToLogs: boolean;
};

export type ConfigValue = {
  appName: string;
  appVersion: string;
  assetsDir: string;
  appMode: 'development' | 'production' | 'staging' | 'local';
  auth: {
    method: 'jwt'  | 'auth0';
    skip: boolean;
    redirectPath: string;
  };
  auth0: { clientId: string; domain: string; callbackUrl: string };
  develop: {
    baseUrl: string;
    appDomainUrl: string;
    candidateJobPortalUrl: string;
    enableInterviewLink: boolean;
  };
  datadog: DatadogConfig;
  /** The ATS Integrations host. A separate service from the main API, so it carries its own origin. */
  integrations: {
    baseUrl: string;
  };
  productFruits: {
    workspaceCode: string;
    enabled: boolean;
  };
  billing: {
    pollIntervalMs: number;
    pollMaxAttemptsSuccess: number;
    pollMaxAttemptsPortal: number;
    pollInitialDelayMs: number;
  };
  googleTagManager: {
    id: string;
  };
};

export type RawConfig = {
  app?: {
    name?: string;
    version?: string;
    assetsDir?: string;
    appMode?: 'development' | 'production' | 'staging' | 'local';
  };
  auth?: {
    method?: string;
    skip?: boolean;
    redirectPath?: string;
  };
  auth0?: {
    clientId?: string;
    domain?: string;
    callbackUrl?: string;
  };
  environment?: {
    baseUrl?: string;
    appDomainUrl?: string;
    candidateJobPortalUrl?: string;
    enableInterviewLink?: boolean;
  };
  datadog?: {
    applicationId?: string;
    clientToken?: string;
    site?: string;
    service?: string;
    env?: string;
    sessionSampleRate?: number;
    sessionReplaySampleRate?: number;
    trackUserInteractions?: boolean;
    trackResources?: boolean;
    trackLongTasks?: boolean;
    defaultPrivacyLevel?: string;
    forwardErrorsToLogs?: boolean;
  };
  integrations?: {
    baseUrl?: string;
  };
  productFruits?: {
    workspaceCode?: string;
    enabled?: boolean;
  };
  googleTagManager?: {
    id?: string;
  };
};

let config: ConfigValue;

/**
 * Config is inlined from VITE_* variables on GCP and fetched from /config.json on Azure.
 * VITE_API_BASE_URL is the switch rather than a per-field default, so a half-set build fails on one
 * missing value instead of quietly serving a mix of the two. Drop the fetch branch once Azure is
 * retired.
 */
export async function loadConfig(): Promise<void> {
  config = transformConfig(
    import.meta.env.VITE_API_BASE_URL ? configFromEnv() : await fetchConfigFile()
  );
}

function configFromEnv(): RawConfig {
  return {
    app: {
      name: import.meta.env.VITE_APP_NAME,
      version: import.meta.env.VITE_APP_VERSION,
      assetsDir: import.meta.env.VITE_ASSETS_DIR,
      appMode: import.meta.env.VITE_APP_MODE,
    },
    auth: {
      method: import.meta.env.VITE_AUTH_METHOD,
      skip: asBoolean(import.meta.env.VITE_AUTH_SKIP),
      redirectPath: import.meta.env.VITE_AUTH_REDIRECT_PATH,
    },
    auth0: {
      clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
      domain: import.meta.env.VITE_AUTH0_DOMAIN,
      callbackUrl: import.meta.env.VITE_AUTH0_CALLBACK_URL,
    },
    environment: {
      baseUrl: import.meta.env.VITE_API_BASE_URL,
      appDomainUrl: import.meta.env.VITE_APP_DOMAIN_URL,
      candidateJobPortalUrl: import.meta.env.VITE_CANDIDATE_JOB_PORTAL_URL,
      enableInterviewLink: asBoolean(import.meta.env.VITE_ENABLE_INTERVIEW_LINK),
    },
    datadog: {
      applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
      clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
      site: import.meta.env.VITE_DATADOG_SITE,
      service: import.meta.env.VITE_DATADOG_SERVICE,
      env: import.meta.env.VITE_DATADOG_ENV,
      sessionSampleRate: asNumber(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE),
      sessionReplaySampleRate: asNumber(import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE),
      trackUserInteractions: asBoolean(import.meta.env.VITE_DATADOG_TRACK_USER_INTERACTIONS),
      trackResources: asBoolean(import.meta.env.VITE_DATADOG_TRACK_RESOURCES),
      trackLongTasks: asBoolean(import.meta.env.VITE_DATADOG_TRACK_LONG_TASKS),
      defaultPrivacyLevel: import.meta.env.VITE_DATADOG_DEFAULT_PRIVACY_LEVEL,
      forwardErrorsToLogs: asBoolean(import.meta.env.VITE_DATADOG_FORWARD_ERRORS_TO_LOGS),
    },
    integrations: {
      baseUrl: import.meta.env.VITE_INTEGRATIONS_BASE_URL,
    },
    productFruits: {
      workspaceCode: import.meta.env.VITE_PRODUCT_FRUITS_WORKSPACE_CODE,
      enabled: asBoolean(import.meta.env.VITE_PRODUCT_FRUITS_ENABLED),
    },
    googleTagManager: {
      id: import.meta.env.VITE_GTM_ID,
    },
  };
}

async function fetchConfigFile(): Promise<RawConfig> {
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error(`Failed to load config.json: ${response.statusText}`);
  }

  return response.json();
}

function asBoolean(value: string | undefined) {
  return value === undefined ? undefined : value === 'true';
}

function asNumber(value: string | undefined) {
  return value === undefined ? undefined : Number(value);
}

export function getConfig() {
  if (!config) {
    throw new Error('Config has not been loaded yet.');
  }
  return config;
}

function transformConfig(rawConfig: RawConfig): ConfigValue {
  return {
    appName: rawConfig.app?.name ?? 'Default App Name',
    appVersion: rawConfig.app?.version ?? '0.0.1',
    assetsDir: rawConfig.app?.assetsDir ?? '',
    appMode: rawConfig.app?.appMode ?? 'development',
    auth: {
      method: rawConfig.auth?.method === 'auth0' ? 'auth0' : 'jwt',
      skip: rawConfig.auth?.skip ?? false,
      redirectPath: rawConfig.auth?.redirectPath ?? '/dashboard',
    },
    auth0: {
      clientId: rawConfig.auth0?.clientId ?? '',
      domain: rawConfig.auth0?.domain ?? '',
      callbackUrl: rawConfig.auth0?.callbackUrl ?? '',
    },
    develop: {
      baseUrl: rawConfig.environment?.baseUrl ?? '',
      appDomainUrl: rawConfig.environment?.appDomainUrl ?? '',
      candidateJobPortalUrl: rawConfig.environment?.candidateJobPortalUrl ?? '',
      enableInterviewLink: rawConfig.environment?.enableInterviewLink ?? false,
    },
    datadog: {
      applicationId: rawConfig.datadog?.applicationId ?? '',
      clientToken: rawConfig.datadog?.clientToken ?? '',
      site: rawConfig.datadog?.site ?? 'datadoghq.com',
      service: rawConfig.datadog?.service ?? 'hello-recruiter',
      env: rawConfig.datadog?.env ?? 'development',
      sessionSampleRate: rawConfig.datadog?.sessionSampleRate ?? 100,
      sessionReplaySampleRate: rawConfig.datadog?.sessionReplaySampleRate ?? 20,
      trackUserInteractions: rawConfig.datadog?.trackUserInteractions ?? true,
      trackResources: rawConfig.datadog?.trackResources ?? true,
      trackLongTasks: rawConfig.datadog?.trackLongTasks ?? true,
      defaultPrivacyLevel: rawConfig.datadog?.defaultPrivacyLevel ?? 'mask-user-input',
      forwardErrorsToLogs: rawConfig.datadog?.forwardErrorsToLogs ?? true,
    },
    integrations: {
      baseUrl: rawConfig.integrations?.baseUrl ?? '',
    },
    productFruits: {
      workspaceCode: rawConfig.productFruits?.workspaceCode ?? '',
      enabled: rawConfig.productFruits?.enabled ?? false,
    },
    billing: {
      pollIntervalMs: 1000,
      pollMaxAttemptsSuccess: 15,
      pollMaxAttemptsPortal: 10,
      pollInitialDelayMs: 500,
    },
    googleTagManager: {
      id: rawConfig.googleTagManager?.id?.trim() ?? '',
    },
  };
}